import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Maximize2, Save } from 'lucide-react';
import api from '../../../utils/api';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

export default function ReportCardTemplateModal({ open, onOpenChange, schoolId, activeTemplateId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [draftId, setDraftId] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [fullOpen, setFullOpen] = useState(false);

  const templates = data?.templates || [];
  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [templates],
  );

  useEffect(() => {
    if (!open || !schoolId) return;
    setLoading(true);
    api.get('/reports/templates', { params: { category: 'report_card', school_id: schoolId } })
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, schoolId]);

  useEffect(() => {
    const next = activeTemplateId || data?.active_template_id || sortedTemplates[0]?.id || '';
    setDraftId(next ? String(next) : '');
  }, [activeTemplateId, data?.active_template_id, sortedTemplates]);

  useEffect(() => {
    if (!open || !draftId || !schoolId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewHtml('');
    api.get('/reports/templates/preview', { params: { template_id: draftId, school_id: schoolId, category: 'report_card' } })
      .then((res) => {
        if (cancelled) return;
        const html = res.data?.data?.html || '';
        setPreviewHtml(html);
        if (!html) setPreviewError('Preview HTML was empty');
      })
      .catch((e) => {
        if (cancelled) return;
        setPreviewError(e?.response?.data?.message || 'Failed to load preview');
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [open, draftId, schoolId]);

  const isDirty = draftId && String(draftId) !== String(activeTemplateId || data?.active_template_id || '');
  const selectedTemplate = sortedTemplates.find((t) => String(t.id) === String(draftId));

  const handleSave = async () => {
    if (!schoolId || !draftId) return;
    setSaving(true);
    try {
      const res = await api.put('/reports/templates/active', {
        school_id: schoolId,
        category: 'report_card',
        template_id: draftId,
      });
      const nextActive = res.data?.data?.active_template_id || draftId;
      onSaved?.(nextActive);
      setData((prev) => (prev ? { ...prev, active_template_id: nextActive } : prev));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Report Card Templates</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid gap-4 md:grid-cols-[320px_1fr]">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Template</Label>
                <Select value={draftId} onValueChange={setDraftId} disabled={!sortedTemplates.length}>
                  <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select template" /></SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {sortedTemplates.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.name || t.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTemplate?.description && (
                  <p className="text-xs text-muted-foreground">{selectedTemplate.description}</p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="button" className={PRIMARY_BTN} disabled={!isDirty || saving} onClick={handleSave}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save as active
                </Button>
                <Button type="button" variant="outline" disabled={!draftId || previewLoading} onClick={() => setFullOpen(true)}>
                  <Maximize2 className="h-4 w-4 mr-2" /> Full preview
                </Button>
              </div>

              <div className="rounded-xl border border-border p-3 bg-muted/10 text-xs text-muted-foreground">
                Active: <span className="font-medium text-foreground">{String(data?.active_template_id || activeTemplateId || '—')}</span>
              </div>
            </div>

            <div className="rounded-xl border border-border overflow-hidden bg-white flex flex-col min-h-0">
              <div className="px-3 py-2 border-b border-border bg-muted/30 text-xs text-muted-foreground">
                Live preview{selectedTemplate?.name ? ` — ${selectedTemplate.name}` : ''}
              </div>
              <div className="flex-1 min-h-0 bg-muted/10">
                {previewLoading ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading preview…
                  </div>
                ) : previewError ? (
                  <div className="h-full flex items-center justify-center text-sm text-red-500 px-4 text-center">{previewError}</div>
                ) : (
                  <iframe title="reportcard-template-preview" className="w-full h-full bg-white" sandbox="allow-same-origin" srcDoc={previewHtml} />
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <ReportCardTemplateFullPreview
      open={fullOpen}
      onOpenChange={setFullOpen}
      title={selectedTemplate?.name || 'Template preview'}
      html={previewHtml}
    />
    </>
  );
}

function ReportCardTemplateFullPreview({ open, onOpenChange, title, html }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader><DialogTitle>{title || 'Template preview'}</DialogTitle></DialogHeader>
        <div className="flex-1 min-h-0 rounded-lg border overflow-hidden bg-white">
          <iframe title="reportcard-template-full" className="w-full h-full bg-white" sandbox="allow-same-origin" srcDoc={html || ''} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

